import { cn } from '@/utils';
import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  /**
   * A reference to the parent element to position the tooltip; if provided,
   * the tooltip will render using React's `createPortal` and append it to the body.
   */
  parentRef?: React.RefObject<HTMLDivElement>;

  /**
   * The text to display in the tooltip.
   */
  text: string;

  /**
   * Additional class names for the tooltip wrapper element.
   */
  className?: string;

  /**
   * Additional class names for the rendered tooltip.
   */
  tooltipClassName?: string;

  /**
   * The children elements that will trigger the tooltip on hover.
   */
  children?: React.ReactNode;

  /**
   * Optional content to display inside the tooltip. This overrides
   * the children prop if both are provided.
   */
  content?: React.ReactNode;
}

/**
 * Tooltip component displays a tooltip with additional information.
 */
export const Tooltip: React.FC<TooltipProps> = ({
  parentRef,
  text,
  className,
  tooltipClassName,
  children,
  content,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  /**
   * Class names for the tooltip element.
   */
  const tooltipClassNames = cn(
    [
      'bg-neutral-white text-neutral-black',
      'cursor-default user-select-none',
      'rounded-[0.25rem]',
      'shadow-[0px_4px_24px_0px_#00000040]',
      'text-[0.6875rem] leading-[0.875rem]',
      'w-[300px] p-3',
    ],
    {
      'absolute left-1/2 mt-2 z-10': !parentRef,
      'fixed z-[50]': parentRef,
    },
    tooltipClassName,
  );

  /**
   * Calculates the position of the tooltip based on the parent element's position.
   * Memoize tooltip position calculation to avoid unnecessary DOM queries.
   */
  const tooltipPosition = useMemo(() => {
    if (!showTooltip || !parentRef || !parentRef.current) {
      return { top: 0, left: 0, visible: false };
    }

    const rect = parentRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    // Tooltip dimensions (should match the CSS width/padding)
    const TOOLTIP_WIDTH = 300;
    const TOOLTIP_HEIGHT = 74; // Approximate, adjust if your tooltip is taller

    // Calculate initial position (centered horizontally, below the parent, with 8px gap)
    let left = rect.left + window.scrollX + rect.width / 2 - TOOLTIP_WIDTH / 2;
    let top = rect.bottom + window.scrollY + 8;

    // Clamp horizontally so tooltip never goes outside viewport
    if (left < 8) left = 8;
    if (left + TOOLTIP_WIDTH > viewportWidth - 8) left = viewportWidth - TOOLTIP_WIDTH - 8;

    // Clamp vertically so tooltip never goes outside viewport
    if (top + TOOLTIP_HEIGHT > viewportHeight + window.scrollY - 8) {
      // If not enough space below, try to show above the parent
      const aboveTop = rect.top + window.scrollY - TOOLTIP_HEIGHT - 8;
      if (aboveTop > window.scrollY + 8) {
        top = aboveTop;
      } else {
        // If not enough space above, clamp to bottom edge
        top = viewportHeight + window.scrollY - TOOLTIP_HEIGHT - 8;
      }
    }

    if (top < window.scrollY + 8) top = window.scrollY + 8;

    return {
      top,
      left,
      visible: true,
    };
  }, [showTooltip, parentRef]);

  /**
   * Content for the tooltip.
   */
  const tooltipContent = <div className={tooltipClassNames}>{text}</div>;

  return (
    <span
      className={cn(className)}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {content ?? children}
      {showTooltip &&
        (() => {
          let portal = null;
          if (parentRef && tooltipPosition.visible) {
            const portalRoot = document?.getElementById('portal-root');
            if (portalRoot) {
              portal = createPortal(
                <div
                  className={tooltipClassNames}
                  style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
                >
                  {text}
                </div>,
                portalRoot,
              );
            }
          }

          return portal ?? tooltipContent;
        })()}
    </span>
  );
};

Tooltip.displayName = 'Tooltip';

export default Tooltip;
